import { useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, FormField, FormHelp, Label, Textarea, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import useMultiplexListingFlow from '../../../../hooks/useMultiplexListingFlow';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil } from '../../../../utils/postingUtils';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { ROOM_FOR_RENT_FLOW } from '../../../../containers/CreateListingPage/constants';
import WizardServerError from '../../../../model/WizardServerError';

import LocaleNotice from '../../../../components/LocaleNotice';
import RecommendationsLearnMoreContent from '../../../../components/RecommendationsLearnMoreContent';
import { WizardContext } from '../../../../components/Wizard/context';
import WizardAlert from '../../../../components/WizardAlert';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type React from 'react';

const fieldName = 'leaseTerms';

const basePlaceholder = `Example: Owner pays for water. Renter is responsible for gas and electric. Last month’s rent due at signing. No smoking allowed. Up to three pets total allowed.`;

const roomForRentPlaceholder = 'Example: No smoking allowed inside.';

type Props = {
  isRelistingFieldset?: boolean;
};

const LeaseTermsFormField = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, roomForRentFlow, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const { leaseTerms, listingAlias, localeSpecificNotice, currentProperty } = useSelector(
    (state) => ({
      leaseTerms: state.properties.currentProperty.leaseTerms,
      listingAlias: state.properties.currentProperty.listingAlias,
      localeSpecificNotice: state.properties.currentProperty.localeSpecificNotice,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const [displayLocalNotice, setDisplayLocalNotice] = useState(false);
  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      leaseTerms,
    },
    postingConstraints,
  );

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });
  const leaseTermsPlaceholder = isRoomForRent ? roomForRentPlaceholder : basePlaceholder;

  useEffect(() => {
    clearServerError();
  }, []);

  useEffect(() => {
    setDisplayLocalNotice(roomForRentFlow !== ROOM_FOR_RENT_FLOW.RYAN_FLOW);
  }, [roomForRentFlow]);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: fieldName,
          // @ts-expect-error
          value: inputs.leaseTerms,
        },
      });
    }
  }, [isSaveComplete]);

  const displayError = serverErrorObj.fieldName === fieldName && serverErrorObj.message !== '';

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const dataToSave = {
      property: {
        [fieldName]: value,
        listingAlias,
      },
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    // for discriminatory content we will let the form field handle displaying the error
    // index of 'comply' indicates a content violation
    const hasDiscriminatoryContent = message[0]?.toLowerCase().indexOf('comply') !== -1;

    // for WizardAlert
    const displayMessage = hasDiscriminatoryContent ? '' : message;

    // for FormHelp
    message = hasDiscriminatoryContent ? message : '';

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage,
      }),
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <div data-test="LeaseTermsFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField>
        <Label>
          Lease terms
          {isRelistingFieldset && (
            <TextButton
              css={{ paddingLeft: '200' }}
              textStyle="body-sm-bold"
              onClick={() => {
                setDisplayRecsModal(true);
              }}
            >
              Learn more
            </TextButton>
          )}
        </Label>
        <Textarea
          name={fieldName}
          value={inputs.leaseTerms as string}
          placeholder={leaseTermsPlaceholder}
          rows={10}
          onChange={handleChange}
          onBlur={save}
          error={displayError}
        />
        <FormHelp error={displayError}>{displayError && serverErrorObj.message}</FormHelp>
      </FormField>

      {displayLocalNotice && (
        <Box css={{ marginBlock: '400' }}>
          <LocaleNotice localeSpecificNotice={localeSpecificNotice} />
        </Box>
      )}

      {isRelistingFieldset && displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={RECS?.LEASE_TERMS}
          isFromRelistingFlow={true}
        />
      )}
    </div>
  );
};

export default LeaseTermsFormField;
